/**
 * NeuNexus Industry 4.0 Blueprint — Executive Strategic Report.
 *
 * Multilingual (DE · EN · PT) executive report page in the style used for
 * European industrial consortiums and Smart Factory investment proposals.
 * Content lives in `@/i18n/report.js`; the language is driven by the shared
 * NeuNexus LangProvider (top-right switcher).
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, ChevronRight, CheckCircle2, Compass, Building2,
  Cog, Rocket, Sparkles, Circle, FileText, Download,
} from "lucide-react";
import { LanguageSwitcher } from "@/i18n/neunexus";
import { useReport, TOC_KEYS } from "@/i18n/report";

const P = {
  bg:      "#080B12",
  bg2:     "#0A0F1A",
  surface: "rgba(255,255,255,0.03)",
  glass:   "rgba(255,255,255,0.045)",
  border:  "rgba(255,255,255,0.08)",
  ink:     "#E8ECF0",
  dim:     "rgba(232,236,240,0.68)",
  silver:  "#C0C7CE",
  green:   "#22C55E",
  blue:    "#3B82F6",
  amber:   "#F59E0B",
  purple:  "#A855F7",
};

// -------------------------------------------------------- Building blocks

const Html = ({ html, as: Tag = "p", className, style }) => (
  <Tag className={className} style={style} dangerouslySetInnerHTML={{ __html: html }} />
);

const Chapter = ({ id, eyebrow, title, children }) => (
  <section id={id} className="scroll-mt-24 pt-14 first:pt-0" data-testid={`chapter-${id}`}>
    <div className="mb-6">
      {eyebrow && (
        <div className="text-[10px] uppercase tracking-[0.34em] font-semibold mb-3" style={{ color: P.silver }}>
          {eyebrow}
        </div>
      )}
      <h2 className="font-semibold tracking-tight text-[26px] lg:text-[32px] leading-tight" style={{ color: "#fff", letterSpacing: "-0.01em" }}>
        {title}
      </h2>
    </div>
    <div className="report-prose">{children}</div>
    <hr className="mt-14" style={{ borderColor: P.border }} />
  </section>
);

const Sub = ({ n, title, children }) => (
  <div className="mt-8 first:mt-0">
    <h3 className="text-[15.5px] font-semibold tracking-tight mb-2" style={{ color: "#fff" }}>
      <span className="mr-2" style={{ color: P.silver, fontVariantNumeric: "tabular-nums" }}>{n}</span>
      {title}
    </h3>
    <div className="text-[13.5px] leading-relaxed" style={{ color: P.dim }}>{children}</div>
  </div>
);

const Callout = ({ tone = "blue", label, html }) => {
  const color = { blue: P.blue, green: P.green, amber: P.amber, purple: P.purple, silver: P.silver }[tone] || P.blue;
  return (
    <div className="my-5 rounded-md p-4" style={{ background: `${color}0d`, border: `1px solid ${color}44` }}>
      {label && (
        <div className="text-[9.5px] uppercase tracking-[0.28em] font-semibold mb-1.5" style={{ color }}>
          {label}
        </div>
      )}
      <Html html={html} className="text-[13px] leading-relaxed" style={{ color: P.ink }} />
    </div>
  );
};

const Table = ({ headers, rows, caption, testid }) => (
  <div className="my-6 overflow-x-auto rounded-lg" style={{ border: `1px solid ${P.border}` }} data-testid={testid}>
    <table className="min-w-full text-[12.5px]" style={{ color: P.ink, borderCollapse: "collapse" }}>
      <thead>
        <tr style={{ background: "rgba(255,255,255,0.03)" }}>
          {headers.map((h, i) => (
            <th key={i} className="text-left px-4 py-2.5 font-semibold" style={{ color: P.silver, borderBottom: `1px solid ${P.border}` }}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((c, j) => (
              <td key={j} className="px-4 py-3 align-top" style={{ borderBottom: `1px solid ${P.border}` }}>
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
    {caption && (
      <div className="px-4 py-2 text-[10.5px] uppercase tracking-[0.24em]" style={{ color: P.silver, background: "rgba(255,255,255,0.02)" }}>
        {caption}
      </div>
    )}
  </div>
);

// -------------------------------------------------------- Phase timeline

// Status labels per language → visual token (color + icon)
const PHASE_ICONS = [Compass, Cog, Building2, Rocket, Sparkles, CheckCircle2];
const STATUS_TOKENS = {
  // English
  "Active": { color: P.green, glow: true, dot: "solid" },
  "Next": { color: P.amber, glow: false, dot: "ring" },
  "Planned": { color: P.silver, glow: false, dot: "ring" },
  "Vision": { color: P.purple, glow: false, dot: "ring" },
  // German
  "Aktiv": { color: P.green, glow: true, dot: "solid" },
  "Nächste": { color: P.amber, glow: false, dot: "ring" },
  "Geplant": { color: P.silver, glow: false, dot: "ring" },
  // Portuguese
  "Ativa": { color: P.green, glow: true, dot: "solid" },
  "Próxima": { color: P.amber, glow: false, dot: "ring" },
  "Planeada": { color: P.silver, glow: false, dot: "ring" },
  "Visão": { color: P.purple, glow: false, dot: "ring" },
};

const PhaseTimeline = ({ rows, caption }) => (
  <figure className="my-8" data-testid="phase-timeline">
    <div className="relative">
      {/* Central vertical rail */}
      <div
        className="absolute left-[27px] top-2 bottom-2 w-px"
        style={{ background: `linear-gradient(180deg, ${P.green}66 0%, ${P.amber}44 18%, ${P.border} 50%, ${P.purple}44 100%)` }}
        aria-hidden="true"
      />
      <ol className="space-y-4">
        {rows.map(([phase, focus, status], i) => {
          const tok = STATUS_TOKENS[status] || { color: P.silver, glow: false, dot: "ring" };
          const Icon = PHASE_ICONS[i] || Circle;
          return (
            <li key={i} className="relative flex gap-5" data-testid={`phase-${i + 1}`}>
              {/* Node */}
              <div className="shrink-0 relative z-10">
                <div
                  className="w-[54px] h-[54px] rounded-full flex items-center justify-center transition"
                  style={{
                    background: tok.dot === "solid" ? `${tok.color}22` : "rgba(8,11,18,0.92)",
                    border: `1.5px solid ${tok.color}${tok.glow ? "" : "88"}`,
                    boxShadow: tok.glow ? `0 0 0 4px ${tok.color}22, 0 0 24px ${tok.color}66` : "none",
                  }}
                >
                  <Icon size={20} style={{ color: tok.color }} strokeWidth={1.75} />
                </div>
              </div>
              {/* Card */}
              <div
                className="flex-1 rounded-md p-4 lg:p-5 transition"
                style={{
                  background: `linear-gradient(90deg, ${tok.color}0a 0%, ${P.surface} 60%)`,
                  border: `1px solid ${tok.color}33`,
                }}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: tok.color }}>
                      {phase}
                    </div>
                    <div className="mt-1 text-[13.5px] leading-relaxed" style={{ color: P.ink }}>
                      {focus}
                    </div>
                  </div>
                  <span
                    className="text-[9.5px] uppercase tracking-[0.24em] font-semibold px-2 py-1 rounded shrink-0"
                    style={{
                      background: `${tok.color}18`,
                      color: tok.color,
                      border: `1px solid ${tok.color}44`,
                    }}
                    data-testid={`phase-${i + 1}-status`}
                  >
                    {status}
                  </span>
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
    {caption && (
      <figcaption className="mt-4 text-[10.5px] uppercase tracking-[0.24em]" style={{ color: P.silver }}>
        {caption}
      </figcaption>
    )}
  </figure>
);

// -------------------------------------------------------- Page

export default function StrategicReport() {
  const { t } = useReport();
  const [active, setActive] = React.useState("executive-summary");

  React.useEffect(() => {
    document.title = `${t.cover.title} · ${t.cover.subtitle}`;
  }, [t.cover.title, t.cover.subtitle]);

  // Auto-scroll to hash target (e.g. /report#chapter-5)
  React.useEffect(() => {
    const scrollToHash = () => {
      const id = window.location.hash.replace("#", "");
      if (!id) return;
      const el = document.getElementById(id);
      if (el) {
        const y = el.getBoundingClientRect().top + window.pageYOffset - 80;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    };
    // Delay to allow layout
    const t1 = setTimeout(scrollToHash, 120);
    window.addEventListener("hashchange", scrollToHash);
    return () => { clearTimeout(t1); window.removeEventListener("hashchange", scrollToHash); };
  }, []);

  React.useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActive(visible[0].target.id);
      },
      { rootMargin: "-25% 0px -60% 0px", threshold: 0 }
    );
    TOC_KEYS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  return (
    <div className="relative min-h-screen" style={{ background: P.bg, color: P.ink }} data-testid="page-report">
      <style>{`
        .report-prose p { font-size: 13.5px; line-height: 1.75; color: ${P.dim}; margin-top: 0.75rem; }
        .report-prose p + p { margin-top: 1rem; }
        .report-prose ul { margin-top: 0.6rem; padding-left: 1.1rem; }
        .report-prose li { font-size: 13.5px; line-height: 1.7; color: ${P.dim}; list-style: disc; margin-top: 0.25rem; }
        .report-prose strong { color: #fff; font-weight: 600; }
        .report-prose em { color: ${P.silver}; font-style: normal; letter-spacing: 0.02em; }
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; color: black !important; }
        }
      `}</style>

      {/* Language switcher (top-right, from shared context) */}
      <LanguageSwitcher />

      {/* Top bar */}
      <div className="no-print sticky top-0 z-40" style={{ background: "rgba(8,11,18,0.85)", borderBottom: `1px solid ${P.border}`, backdropFilter: "blur(12px)" }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-3 flex items-center justify-between gap-4">
          <Link to="/neunexus" className="flex items-center gap-2 text-[12px]" style={{ color: P.silver }} data-testid="report-back-home">
            <ChevronRight size={13} className="rotate-180" />
            <span className="uppercase tracking-[0.28em]">{t.topbar.back}</span>
          </Link>
          <div className="hidden md:block text-[11px] uppercase tracking-[0.28em]" style={{ color: P.silver }}>
            {t.topbar.ctx}
          </div>
          <div className="flex items-center gap-2">
            <Link to="/reports" data-testid="report-index-link"
              className="hidden md:inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.24em] px-2.5 py-1.5 rounded-md transition hover:bg-white/[0.05]"
              style={{ border: `1px solid ${P.border}`, color: P.silver }}>
              <FileText size={11} /> Reports
            </Link>
            <a
              href="/downloads/The_Autonomous_Manufacturing_Blueprint.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="report-pdf-download"
              className="inline-flex items-center gap-2 text-[11.5px] uppercase tracking-[0.24em] px-3 py-1.5 rounded-md transition hover:bg-white/[0.05]"
              style={{ border: `1px solid ${P.amber}55`, color: P.amber }}
            >
              <Download size={12} /> PDF
            </a>
          </div>
        </div>
      </div>

      {/* Cover */}
      <header className="border-b" style={{ borderColor: P.border }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
          <div className="text-[10px] uppercase tracking-[0.42em] font-semibold" style={{ color: P.green }}>
            {t.cover.platform}
          </div>
          <h1 className="mt-6 font-semibold tracking-tight text-[32px] sm:text-[42px] lg:text-[56px] leading-[1.05]" style={{ color: "#fff", letterSpacing: "-0.015em" }}>
            {t.cover.title}
          </h1>
          <div className="mt-4 text-[18px] lg:text-[22px] font-light" style={{ color: P.ink }}>
            {t.cover.subtitle}
          </div>
          <div className="mt-2 text-[13.5px]" style={{ color: P.silver }}>
            {t.cover.tagline}
          </div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-3xl">
            {t.cover.meta.map((m, i) => (
              <div key={i} className="rounded-md p-3.5" style={{ background: P.glass, border: `1px solid ${P.border}` }}>
                <div className="text-[9.5px] uppercase tracking-[0.28em]" style={{ color: P.silver }}>{m.k}</div>
                <div className="mt-1 text-[13px]" style={{ color: "#fff" }}>{m.v}</div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-[11px] uppercase tracking-[0.32em]" style={{ color: P.silver }}>
            {t.cover.prepared}
          </div>
        </div>
      </header>

      {/* Body: TOC + content */}
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14 grid lg:grid-cols-[240px_1fr] gap-12">
        {/* TOC */}
        <aside className="no-print hidden lg:block">
          <div className="sticky top-24">
            <div className="text-[9.5px] uppercase tracking-[0.32em] font-semibold mb-4" style={{ color: P.silver }}>
              {t.toc.contents}
            </div>
            <nav className="space-y-1" data-testid="report-toc">
              {TOC_KEYS.map((id, idx) => (
                <a
                  key={id}
                  href={`#${id}`}
                  data-testid={`toc-${id}`}
                  className="block text-[12px] py-1.5 border-l-2 pl-3 transition"
                  style={{
                    borderColor: active === id ? P.green : "transparent",
                    color: active === id ? "#fff" : P.silver,
                  }}
                >
                  {t.toc.labels[idx]}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Report body */}
        <main className="max-w-3xl">

          {/* Executive Summary */}
          <Chapter id="executive-summary" eyebrow={t.execSummary.eyebrow} title={t.execSummary.title}>
            <Html html={t.execSummary.p1} />
            <Html html={t.execSummary.p2} />
            <Callout tone="green" label={t.execSummary.calloutLabel} html={t.execSummary.callout} />
            {t.execSummary.subs.map((s) => (
              <Sub key={s.n} n={s.n} title={s.t}><Html as="span" html={s.b} /></Sub>
            ))}
          </Chapter>

          {/* Chapter 1 */}
          <Chapter id="chapter-1" eyebrow={t.ch1.eyebrow} title={t.ch1.title}>
            <Html html={t.ch1.intro} />
            {t.ch1.subs.map((s) => (
              <Sub key={s.n} n={s.n} title={s.t}><Html as="span" html={s.b} /></Sub>
            ))}
          </Chapter>

          {/* Chapter 2 */}
          <Chapter id="chapter-2" eyebrow={t.ch2.eyebrow} title={t.ch2.title}>
            <Html html={t.ch2.intro} />
            <Table testid="table-challenges" caption={t.ch2.table.caption} headers={t.ch2.table.headers} rows={t.ch2.table.rows} />
          </Chapter>

          {/* Chapter 3 */}
          <Chapter id="chapter-3" eyebrow={t.ch3.eyebrow} title={t.ch3.title}>
            <Html html={t.ch3.intro} />
            {t.ch3.subs.map((s) => (
              <Sub key={s.n} n={s.n} title={s.t}><Html as="span" html={s.b} /></Sub>
            ))}
          </Chapter>

          {/* Chapter 4 */}
          <Chapter id="chapter-4" eyebrow={t.ch4.eyebrow} title={t.ch4.title}>
            <Html html={t.ch4.intro} />
            {t.ch4.partners.map((p) => (
              <Sub key={p.n} n={p.n} title={p.t}>
                {p.rows.map((r, i) => (
                  <p key={i}><strong>{r.k}.</strong> {r.v}</p>
                ))}
              </Sub>
            ))}
          </Chapter>

          {/* Chapter 5 */}
          <Chapter id="chapter-5" eyebrow={t.ch5.eyebrow} title={t.ch5.title}>
            <Html html={t.ch5.intro} />
            <Table testid="table-architecture" caption={t.ch5.table.caption} headers={t.ch5.table.headers} rows={t.ch5.table.rows} />
            <Html html={t.ch5.closing} />
          </Chapter>

          {/* Chapter 6 */}
          <Chapter id="chapter-6" eyebrow={t.ch6.eyebrow} title={t.ch6.title}>
            <Html html={t.ch6.intro} />
            <Table testid="table-workflow" caption={t.ch6.table.caption} headers={t.ch6.table.headers} rows={t.ch6.table.rows} />
          </Chapter>

          {/* Chapter 7 */}
          <Chapter id="chapter-7" eyebrow={t.ch7.eyebrow} title={t.ch7.title}>
            <Html html={t.ch7.intro} />
            <Table testid="table-raci" caption={t.ch7.table.caption} headers={t.ch7.table.headers} rows={t.ch7.table.rows} />
            <Html html={t.ch7.legend} className="text-[11.5px]" style={{ color: P.silver }} />
          </Chapter>

          {/* Chapter 8 */}
          <Chapter id="chapter-8" eyebrow={t.ch8.eyebrow} title={t.ch8.title}>
            <ul>
              {t.ch8.bullets.map((b, i) => (<li key={i} dangerouslySetInnerHTML={{ __html: b }} />))}
            </ul>
          </Chapter>

          {/* Chapter 9 */}
          <Chapter id="chapter-9" eyebrow={t.ch9.eyebrow} title={t.ch9.title}>
            {t.ch9.subs.map((s) => (
              <Sub key={s.n} n={s.n} title={s.t}><Html as="span" html={s.b} /></Sub>
            ))}
          </Chapter>

          {/* Chapter 10 */}
          <Chapter id="chapter-10" eyebrow={t.ch10.eyebrow} title={t.ch10.title}>
            <ul>
              {t.ch10.bullets.map((b, i) => (<li key={i} dangerouslySetInnerHTML={{ __html: b }} />))}
            </ul>
          </Chapter>

          {/* Chapter 11 */}
          <Chapter id="chapter-11" eyebrow={t.ch11.eyebrow} title={t.ch11.title}>
            <PhaseTimeline rows={t.ch11.table.rows} caption={t.ch11.table.caption} />
          </Chapter>

          {/* Chapter 12 */}
          <Chapter id="chapter-12" eyebrow={t.ch12.eyebrow} title={t.ch12.title}>
            {t.ch12.subs.map((s) => (
              <Sub key={s.n} n={s.n} title={s.t}><Html as="span" html={s.b} /></Sub>
            ))}
            <Sub n="12.6" title={t.ch12.risksTitle.replace("12.6 ", "")}>
              <ul>
                {t.ch12.risks.map((r, i) => (<li key={i} dangerouslySetInnerHTML={{ __html: r }} />))}
              </ul>
            </Sub>
          </Chapter>

          {/* Chapter 13 */}
          <Chapter id="chapter-13" eyebrow={t.ch13.eyebrow} title={t.ch13.title}>
            <Html html={t.ch13.p} />
            <Callout tone="green" label={t.ch13.calloutLabel} html={t.ch13.callout} />
            <ul>
              {t.ch13.bullets.map((b, i) => (<li key={i} dangerouslySetInnerHTML={{ __html: b }} />))}
            </ul>
          </Chapter>

          {/* Technical Annex */}
          <Chapter id="annex" eyebrow={t.annex.eyebrow} title={t.annex.title}>
            <Html html={t.annex.intro} />
            {t.annex.items.map((it) => (
              <Sub key={it.n} n={it.n} title={it.t}>
                {it.bullets && (
                  <ul>{it.bullets.map((b, i) => (<li key={i} dangerouslySetInnerHTML={{ __html: b }} />))}</ul>
                )}
                {it.body && <Html as="p" html={it.body} />}
                {it.opsLink && (
                  <div className="mt-3">
                    <Link to="/ops" data-testid={`annex-${it.n}-ops-link`} className="inline-flex items-center gap-2 text-[12px] font-semibold" style={{ color: P.blue }}>
                      {it.opsLink} <ArrowUpRight size={12} />
                    </Link>
                  </div>
                )}
              </Sub>
            ))}
          </Chapter>

          {/* Glossary */}
          <Chapter id="glossary" eyebrow={t.glossary.eyebrow} title={t.glossary.title}>
            <Table testid="table-glossary" headers={t.glossary.headers} rows={t.glossary.rows} />
          </Chapter>

          {/* Footer note */}
          <div className="mt-10 text-[10.5px] uppercase tracking-[0.28em]" style={{ color: P.silver }}>
            {t.footer}
          </div>
        </main>
      </div>
    </div>
  );
}
